/**
 * Picking and creating lookalikes.
 *
 * A lookalike is a reusable face: whoever records every week picks instead of uploading
 * again. There are three tabs — the ones the user created, the ready-made library, and
 * creation — and whoever opened the modal gets back only the chosen avatar's id. No image
 * or audio URL travels back to the caller: the server is what resolves what reaches
 * generation.
 */
angular.module("galilai.creator").controller("AvatarPickerController", function ($scope, api, $rootScope, $timeout, transcribeService) {
    // The sample is short on purpose: 30 seconds is enough for the clone, and longer audio
    // only raises the chance of noise coming along
    const SAMPLE_SECONDS = 30;
    const TABS = ["mine", "catalog", "create"];

    const BASE_STATE = {
        tab: "mine",
        loading: false,
        saving: false,
        projectId: null,
        // Quem abriu o modal, para devolver a escolha ao lugar certo
        origin: null,
        mine: [],
        catalog: [],
        voiceSelection: false,
        // Filtros da biblioteca
        filters: { gender: null, ageRange: null, style: null, language: null },
        selected: null,
        selectedVoice: null,
        // Create/edit tab
        draft: {
            // Filled in when editing an existing lookalike
            id: null,
            name: "",
            photos: [],
            voice: null,
            // Voice is optional and ships off: most people only want the face, and having
            // it on by default would show a large block nobody asked for
            voiceEnabled: false,
            // null | 'record' | 'upload' — the screen for the chosen way only appears
            // after the choice
            voiceMode: null,
            recording: false,
            uploading: false,
        },
    };

    let recorder = null;

    $scope.avatar = angular.copy(BASE_STATE);

    function reset() {
        $scope.avatar = angular.copy(BASE_STATE);
        recorder = null;
    }

    // The modal underneath (create video) stays open behind. Pushing it visually back is
    // what keeps the two titles from competing for attention.
    function dimModalsBehind(dim) {
        const modals = document.querySelectorAll(".modal.show");
        modals.forEach(function (modal) {
            if (modal.id === "avatar-picker-modal") {
                return;
            }
            modal.classList.toggle("is-dimmed", dim);
        });
    }

    $rootScope.$on("avatar.picker.show", function (event, data) {
        reset();
        $scope.avatar.projectId = data?.projectId || null;
        $scope.avatar.origin = data?.origin || null;
        $scope.avatar.editAvatarId = data?.editAvatarId || null;
        $scope.load();
        const element = angular.element("#avatar-picker-modal");
        element.modal("show");
        dimModalsBehind(true);
        element.off("hidden.bs.modal.avatar").on("hidden.bs.modal.avatar", function () {
            dimModalsBehind(false);
        });
    });

    $scope.load = function () {
        $scope.avatar.loading = true;
        api.listAvatars($scope.avatar.projectId)
            .then(function (response) {
                $scope.avatar.mine = response?.data?.mine || [];
                $scope.avatar.catalog = response?.data?.catalog || [];
                // Catalogue voice choice is the server's decision: without this, the
                // preset block would show up in a setup where it does nothing
                $scope.avatar.voiceSelection = Boolean(response?.data?.voiceSelection);
                // Whoever has no lookalike yet lands straight on creation
                if ($scope.avatar.mine.length === 0 && $scope.avatar.catalog.length === 0) {
                    $scope.avatar.tab = "create";
                }
                // Came from "other photo" on a single-photo lookalike: what they want is
                // to add more
                if ($scope.avatar.editAvatarId) {
                    const target = $scope.avatar.mine.find(function (item) {
                        return item._id === $scope.avatar.editAvatarId;
                    });
                    $scope.avatar.editAvatarId = null;
                    if (target) {
                        $scope.editAvatar(target);
                    }
                }
            })
            .catch(function (error) {
                console.error("Error loading avatars:", error);
            })
            .finally(function () {
                $scope.avatar.loading = false;
                $timeout(angular.noop);
            });
    };

    $scope.setTab = function (tab) {
        if (TABS.indexOf(tab) === -1) {
            return;
        }
        // Leaving the edit tab drops the draft: coming back later to find somebody else's
        // lookalike half filled in would be worse than starting over
        if (tab !== $scope.avatar.tab) {
            $scope.avatar.draft = angular.copy(BASE_STATE.draft);
        }
        $scope.avatar.tab = tab;
        $scope.avatar.selected = null;
        $scope.avatar.selectedVoice = null;
    };

    // ------------------------------------------------------------------ escolha

    $scope.selectAvatar = function (avatar) {
        $scope.avatar.selected = avatar;
        $scope.avatar.selectedVoice = null;

        // With no voice choice there is no second step: picking the face picks everything
        const needsVoiceStep = $scope.avatar.voiceSelection
            && avatar.catalog
            && avatar.voices
            && avatar.voices.length > 0;
        if (!needsVoiceStep) {
            $scope.confirmAvatar();
            return;
        }
        $scope.avatar.selectedVoice = avatar.voices[0].id;
    };

    // The id here is the voice OPTION's (default, calmer, firmer) — the provider's voice
    // id never reaches the browser
    $scope.selectVoice = function (optionId) {
        $scope.avatar.selectedVoice = optionId;
    };

    $scope.voiceLabel = function (voice) {
        return gettext("avatar.voice_" + voice.id) || voice.id;
    };

    // The preview is a static file, generated once when the catalogue was built. Doing it
    // by calling the provider on every click would turn curiosity into cost.
    $scope.playVoiceSample = function (voice, $event) {
        if ($event) {
            $event.stopPropagation();
        }
        if (!voice.sampleURL) {
            return;
        }
        const audio = new Audio(voice.sampleURL);
        audio.play().catch(function () {});
    };

    $scope.confirmAvatar = function () {
        const avatar = $scope.avatar.selected;
        if (!avatar) {
            return;
        }
        $rootScope.$broadcast("avatar.selected", {
            avatar: avatar,
            voiceOption: $scope.avatar.selectedVoice,
            origin: $scope.avatar.origin,
        });
        angular.element("#avatar-picker-modal").modal("hide");
    };

    $scope.editAvatar = function (avatar, $event) {
        if ($event) {
            $event.stopPropagation();
        }
        $scope.avatar.draft = {
            id: avatar._id,
            name: avatar.name,
            // The photo keeps mediaId and url, which is what editing needs to send back
            photos: (avatar.medias || []).map(function (media) {
                return { mediaId: media.mediaId, url: media.url };
            }),
            // `url` is what makes the saved sample playable: without it the only way to hear
            // your own voice back was to record it again, which is the opposite of checking
            voice: avatar.voiceSource === "own"
                ? { existing: true, url: avatar.voiceSampleURL || null }
                : null,
            voiceEnabled: avatar.voiceSource === "own",
            voiceMode: null,
            recording: false,
            uploading: false,
        };
        $scope.avatar.tab = "create";
    };

    $scope.isEditing = function () {
        return Boolean($scope.avatar.draft.id);
    };

    $scope.removeAvatar = function (avatar, $event) {
        if ($event) {
            $event.stopPropagation();
        }
        if (!confirm(gettext("avatar.remove_confirm"))) {
            return;
        }
        api.removeAvatar(avatar._id)
            .then(function () {
                $scope.load();
            })
            .catch(function (error) {
                console.error("Error removing avatar:", error);
            });
    };

    // ------------------------------------------------------------------ filtros

    $scope.setFilter = function (field, value) {
        $scope.avatar.filters[field] = $scope.avatar.filters[field] === value ? null : value;
    };

    $scope.filteredCatalog = function () {
        const filters = $scope.avatar.filters;
        return $scope.avatar.catalog.filter(function (avatar) {
            return Object.keys(filters).every(function (field) {
                return !filters[field] || avatar[field] === filters[field];
            });
        });
    };

    // Only offers filters that exist in the catalogue: a pill that filters nothing is noise
    $scope.filterValues = function (field) {
        const values = [];
        $scope.avatar.catalog.forEach(function (avatar) {
            if (avatar[field] && values.indexOf(avatar[field]) === -1) {
                values.push(avatar[field]);
            }
        });
        return values;
    };

    $scope.filterLabel = function (field, value) {
        return gettext("avatar.filter_" + field + "_" + value) || value;
    };

    // ------------------------------------------------------------------ creation

    /**
     * The photo comes from the media picker, not from the file explorer.
     *
     * It looks like one step more and it is the opposite: inside it are the upload, the
     * images the user already has and the ones AI generated — including the generated and
     * never used ones, which were exactly what got lost while the lookalike photo was
     * invisible to the library.
     */
    $scope.choosePhotos = function () {
        $rootScope.$broadcast("media.show", {
            source: { origin: "avatarpicker.photos" },
            projectId: $scope.avatar.projectId,
            kind: "image",
            multiple: true,
            openModal: true,
            // Only the tabs whose images carry a media id: a stock photo cannot become a
            // lookalike's face, and picking one and having nothing happen is worse than
            // not being able to pick it
            tabs: ["user", "generated", "favorite"],
            // Above this modal (250000), which is where it was opened from
            zIndex: 260000,
        });
    };

    $rootScope.$on("media.selected", function (event, medias, source) {
        if (!source || source.origin !== "avatarpicker.photos" || !medias) {
            return;
        }
        const valid = medias.filter(function (media) { return media && media._id && media.url; });
        if (valid.length < medias.length) {
            // Should not happen with the tabs restricted, but silence here is the worst
            // possible outcome: the user picks and nothing shows up
            showError(gettext("avatar.photo_not_supported"));
        }
        const chosen = valid
            .map(function (media) { return { mediaId: media._id, url: media.url }; });
        // Picking the same photo twice must not duplicate it on the lookalike
        const jaTem = $scope.avatar.draft.photos.map(function (photo) { return String(photo.mediaId); });
        $scope.avatar.draft.photos = $scope.avatar.draft.photos.concat(
            chosen.filter(function (photo) { return jaTem.indexOf(String(photo.mediaId)) === -1; })
        );
        $timeout(angular.noop);
    });

    // Clicking the thumbnail picks the main photo, so the × has to stop propagation —
    // otherwise removing a photo would also change the main one along the way
    /**
     * Photo variation: it is the same image studio as always, opened with the main photo
     * as its source. It reuses the modal, the quality tiers and the charging — the image
     * lands in the library like any other generation.
     */
    $scope.generateVariation = function () {
        const photos = $scope.avatar.draft.photos;
        if (photos.length === 0) {
            return;
        }
        $rootScope.$broadcast("media.studio.show", {
            mode: "modify",
            origin: "avatarpicker",
            kind: "avatar",
            source: { _id: photos[0].mediaId, url: photos[0].url },
            projectId: $scope.avatar.projectId,
        });
    };

    $rootScope.$on("media.studio.use", function (event, media) {
        if (!media || media.origin !== "avatarpicker") {
            return;
        }
        // Comes in as one more photo, never as the main one: the variation is one more
        // option, and swapping the face on our own would be deciding for the user
        $scope.avatar.draft.photos.push({ mediaId: media._id, url: media.data.url });
        $timeout(angular.noop);
    });

    $scope.removePhoto = function (index, $event) {
        if ($event) {
            $event.stopPropagation();
        }
        $scope.avatar.draft.photos.splice(index, 1);
    };

    // The first photo is the main one: reordering is how the lookalike's face is chosen
    $scope.makeMainPhoto = function (index) {
        if (index === 0) {
            return;
        }
        const photos = $scope.avatar.draft.photos;
        const chosen = photos.splice(index, 1)[0];
        photos.unshift(chosen);
    };

    $scope.sampleText = function () {
        return gettext("avatar.sample_text");
    };

    $scope.toggleVoice = function () {
        $scope.avatar.draft.voiceEnabled = !$scope.avatar.draft.voiceEnabled;
        if (!$scope.avatar.draft.voiceEnabled) {
            $scope.avatar.draft.voiceMode = null;
            $scope.avatar.draft.voice = null;
        }
    };

    // Recording and uploading are different paths and each has its own screen. Showing
    // both at once is what made the block heavy.
    $scope.setVoiceMode = function (mode) {
        $scope.avatar.draft.voiceMode = mode;
        if (mode === "upload") {
            $scope.chooseVoiceFile();
        }
    };

    $scope.toggleRecording = function () {
        if (!recorder) {
            recorder = transcribeService.createRecorder({
                maxSeconds: SAMPLE_SECONDS,
                onState: function (state) {
                    $scope.avatar.draft.recording = state.recording;
                },
                onBlob: function (blob) {
                    uploadVoice(blob, "gravacao.webm");
                },
                onError: function (key) {
                    showError(gettext(key));
                },
            });
        }
        recorder.toggle();
    };

    $scope.chooseVoiceFile = function () {
        const input = document.getElementById("avatar-voice-input");
        if (input) {
            input.value = "";
            input.click();
        }
    };

    $scope.uploadVoiceFile = function (files) {
        if (!files || files.length === 0) {
            return;
        }
        uploadVoice(files[0], files[0].name);
    };

    function uploadVoice(blob, filename) {
        $scope.avatar.draft.uploading = true;
        api.uploadAvatarFiles([blob], "voice", $scope.avatar.projectId, filename)
            .then(function (response) {
                const medias = response?.data?.medias || [];
                if (medias.length === 0) {
                    showError(gettext("avatar.upload_error"));
                    return;
                }
                $scope.avatar.draft.voice = medias[0];
            })
            .catch(function (error) {
                console.error("Error uploading voice:", error);
                showError(gettext("avatar.upload_error"));
            })
            .finally(function () {
                $scope.avatar.draft.uploading = false;
                $timeout(angular.noop);
            });
    }

    $scope.removeVoiceSample = function () {
        $scope.avatar.draft.voice = null;
    };

    // Listening before saving is not a comfort: a bad clone contaminates every video of
    // that lookalike, and finding out later costs a whole video
    let voicePlayback = null;
    $scope.playVoiceSampleDraft = function () {
        if (!$scope.avatar.draft.voice || !$scope.avatar.draft.voice.url) {
            return;
        }
        // A second click restarts rather than stacking a second voice over the first, which
        // is what made a quiet recording sound like a crowd
        if (voicePlayback) {
            voicePlayback.pause();
        }
        voicePlayback = new Audio($scope.avatar.draft.voice.url);
        voicePlayback.play().catch(function () {});
    };

    $scope.canSaveAvatar = function () {
        const draft = $scope.avatar.draft;
        return !draft.uploading && !draft.recording && !$scope.avatar.saving && draft.photos.length > 0;
    };

    $scope.saveAvatar = function () {
        if (!$scope.canSaveAvatar()) {
            return;
        }
        const draft = $scope.avatar.draft;
        $scope.avatar.saving = true;

        const payload = {
            projectId: $scope.avatar.projectId,
            name: draft.name,
            mediaIds: draft.photos.map(function (photo) { return photo.mediaId; }),
            voiceSampleMediaId: draft.voice ? draft.voice.mediaId : null,
        };
        // Editing without recording again keeps the voice that was already there:
        // `existing` marks that, and without the mark a missing mediaId would read as
        // "delete the voice"
        if ($scope.isEditing()) {
            payload.removeVoice = !draft.voiceEnabled || (!draft.voice && !draft.voiceMode);
            if (draft.voice && draft.voice.existing) {
                payload.voiceSampleMediaId = null;
                payload.removeVoice = false;
            }
        }

        const request = $scope.isEditing()
            ? api.updateAvatar(draft.id, payload)
            : api.createAvatar(payload);

        request
            .then(function (response) {
                if (!response?.data?.success) {
                    showError(gettext("avatar.save_error"));
                    return;
                }
                if ($scope.isEditing()) {
                    // Whoever edited wanted to fix the lookalike, not necessarily use it now
                    $scope.avatar.draft = angular.copy(BASE_STATE.draft);
                    $scope.avatar.tab = "mine";
                    $scope.load();
                    return;
                }
                // A just-created one is what the user wants now: select it for them
                $scope.avatar.selected = response.data.avatar;
                $scope.confirmAvatar();
            })
            .catch(function (error) {
                console.error("Error saving avatar:", error);
                showError(gettext("avatar.save_error"));
            })
            .finally(function () {
                $scope.avatar.saving = false;
                $timeout(angular.noop);
            });
    };
});
